import { ONE_SECOND_IN_MILISECONDS, TEN_SECONDS_IN_MS } from 'constants/timers';
import { Success as SuccessIcon } from 'icons';
import { ReactComponent as ShareIcon } from 'icons/icon-share.svg';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { createMarkup, useTranslations } from 'utils';

import './CreatePlanSuccess.scss';
import { useEmbeddedProps } from 'hooks/useEmbeddedProps/useEmbeddedProps';
import { useFieldValues } from 'hooks/useFieldValues/useFieldValues';
import { useSelector } from 'react-redux';
import {
  getRecurringOffersMetadata,
  getSelectedRecurringOffer,
} from 'selectors/entities/offers/offers';
import { date } from 'utils/date/date';

type SuccessWithRedirectionProps = {
  planId: string;
  email: string;
  redirectionTimeout?: number;
};

export const CreatePlanSuccess = ({
  email,
  planId,
  redirectionTimeout = TEN_SECONDS_IN_MS,
}: PropsWithChildren<SuccessWithRedirectionProps>) => {
  const [countdown, setCountdown] = useState(redirectionTimeout / 1000);
  const { schedule } = useSelector(getRecurringOffersMetadata);
  const { installments } = useSelector(getSelectedRecurringOffer);

  const { return_url: returnUrl, session_id: consumerSessionId } =
    useFieldValues();

  const { fwFinish } = useEmbeddedProps();
  const i18n = useTranslations();

  const shouldRedirect = returnUrl && fwFinish;
  const redirectUrl = shouldRedirect && buildUrl(returnUrl, planId);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(countdown - 1);
    }, ONE_SECOND_IN_MILISECONDS);

    if (countdown === 0 && shouldRedirect) {
      clearInterval(timer);
      fwFinish(redirectUrl);
    }

    return () => {
      timer && clearInterval(timer);
    };
  }, [countdown, fwFinish, redirectUrl, shouldRedirect]);

  const description =
    schedule === 'pay_now'
      ? i18n.t('checkout.createPlan.success.description_pay_now')
      : i18n.t('checkout.createPlan.success.description_pay_later', {
          date: date.formatToBrowserLocale(installments[0].date),
        });

  return (
    <div className="CreatePlanSuccess" data-testid="CreatePlanSuccess">
      <div className="CreatePlanSuccess-Content">
        <SuccessIcon width={64} height={64} />
        <h2>{i18n.t('checkout.createPlan.success.title')}</h2>
        <p>{description}</p>
        {!consumerSessionId && (
          <p
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('checkout.createPlan.success.tracking_link', {
                email,
              }),
            )}
          />
        )}
        {!consumerSessionId && (
          <a
            data-testid="trackMyPaymentLink"
            target="_blank"
            rel="noreferrer"
            title={i18n.t('checkout.createPlan.trackMyPayment') as string}
            href="/receivables"
          >
            {i18n.t('checkout.createPlan.trackMyPayment')} <ShareIcon />
          </a>
        )}
      </div>
      {shouldRedirect && (
        <span className="CreatePlanSuccess-Countdown">
          {i18n.t('redirectCountdown.countdown', {
            countdown: `${countdown}`,
          })}
          ...
        </span>
      )}
    </div>
  );
};

const PLAN_ID = 'plan_id';

const buildUrl = (redirectUrl: string, planId: string) => {
  const url = new URL(redirectUrl);
  url.searchParams.append(PLAN_ID, planId);

  return url.toString();
};
